// Opening move: Shared EditorRefPlugin for exposing the Lexical editor instance
// This plugin captures the live LexicalEditor from the composer context and
// surfaces it via callback so consumers can access editor methods programmatically

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { LexicalEditor } from 'lexical'

export interface EditorRefPluginProps {
  onEditorRef: (editor: LexicalEditor) => void
}

/**
 * EditorRefPlugin - Capture and expose Lexical editor instance
 *
 * Main play: Provides a stable ref to the LexicalEditor for external consumers
 * Used by both baseline and legacy editor paths to enable:
 * - Test automation (Playwright commands)
 * - Programmatic content manipulation
 * - Editor method access (focus, selection, etc.)
 *
 * Big play: Plugin fires exactly once per composer lifecycle
 * Time to tackle the tricky bit: Ensures callback is stable and no memory leaks
 */
export default function EditorRefPlugin({ onEditorRef }: EditorRefPluginProps): null {
  const [editor] = useLexicalComposerContext()

  // Main play: emit editor reference once when component mounts
  useEffect(() => {
    // Opening move: call the callback with the editor instance
    onEditorRef(editor)

    // Victory lap: cleanup function for proper plugin lifecycle
    // Note: No explicit cleanup needed as we don't register listeners
    return () => {
      // Reserved for future cleanup if needed (e.g., command unregistration)
    }
  }, [editor, onEditorRef])

  // Victory lap: plugin has no UI, just captures the editor reference
  return null
}
